<template>
    <base-form
        ref="form"
        v-model="form"
        :api="api"
        :mapper="mapper"
        :entity-id="entityId"
        :title="title"
        :disabled="disabled"
        @created="created($event)"
        @updated="updated($event)"
        @cancelled="cancelled"
        @deleted="deleted"
    >
        <form-panel>
            <form-two-col-row>
                <template #col1>
                    <text-input
                        v-model="form.name"
                        v-validate="'required|max:255|space'"
                        name="name"
                        label="labels.name"
                        :error-messages="errors.collect('name')"
                        :data-vv-as="$t('labels.name')"
                    ></text-input>
                    <textarea-input
                        v-model="form.description"
                        v-validate="'required|space'"
                        name="description"
                        label="labels.description"
                        :error-messages="errors.collect('description')"
                        :data-vv-as="$t('labels.description')"
                    ></textarea-input>
                    <entity-select-input
                        v-validate="'required'"
                        :value="form.restaurants"
                        multi-select
                        label="labels.restaurants"
                        :data-vv-as="$t('labels.restaurants')"
                        :entity="selectApiClientType.RESTAURANT"
                        name="restaurants"
                        autocomplete
                        :items-filter="restaurantItemsFilter"
                        :needs-update="restaurantsNeedUpdate"
                        :error-messages="errors.collect('restaurants')"
                        @input="onRestaurantsInput"
                    ></entity-select-input>
                    <confirm-modal
                        v-model="storeWarningDialog"
                        :text="
                            $t(
                                'texts.store_restaurant_removed_from_store_warning'
                            )
                        "
                        @onApprove="applyPendingRestaurants"
                        @onCancel="discardPendingRestaurants"
                    ></confirm-modal>
                </template>
                <template #col2>
                    <image-editor
                        v-model="form"
                        :primary-size="{ width: 240, height: 240 }"
                        primary-key="logo"
                        primary-label="labels.logo"
                        :quality="0.9"
                    ></image-editor>
                    <image-editor
                        v-model="form"
                        class="mt-4"
                        :primary-size="{ width: 1200, height: 720 }"
                        primary-key="cover"
                        primary-label="labels.cover_image"
                        :quality="0.9"
                    ></image-editor>
                </template>
            </form-two-col-row>
            <form-one-col-row>
                <v-input
                    v-model="form.tags"
                    v-validate="'required'"
                    name="tags"
                    :error-messages="errors.collect('tags')"
                    :data-vv-as="$t('labels.tags')"
                >
                    <v-text-field
                        v-model="newTag"
                        :label="$t('labels.add_tag')"
                        append-outer-icon="add"
                        @keyup.enter="addTag"
                        @click:append-outer="addTag"
                    ></v-text-field>
                </v-input>
                <div class="tags">
                    <v-chip
                        v-for="tag in form.tags"
                        :key="tag"
                        class="mr-1 my-1"
                        close
                        @click:close="removeTag(tag)"
                    >
                        {{ tag }}
                    </v-chip>
                </div>
            </form-one-col-row>
        </form-panel>
    </base-form>
</template>

<script>
import routeType from '@/router/routeType'
import RestApiType from '@/api/RestApiType'
import MapperType from '@/services/mapper/MapperType'
import FormBaseMixin from '@/components/mixins/FormBase'
import FormPanel from '@/components/form/FormPanel.vue'
import FormOneColRow from '@/components/form/FormOneColRow.vue'
import FormTwoColRow from '@/components/form/FormTwoColRow.vue'
import TextInput from '@/components/form/Inputs/TextInput.vue'
import TextareaInput from '@/components/form/Inputs/TextareaInput.vue'
import ImageEditor from '@/components/pages/meals/ImageEditor.vue'
import EntitySelectInput from '@/components/form/Inputs/EntitySelectInput.vue'
import ConfirmModal from '@/components/confirm/ConfirmModal.vue'

export default {
    components: {
        EntitySelectInput,
        FormPanel,
        FormOneColRow,
        FormTwoColRow,
        TextInput,
        TextareaInput,
        ImageEditor,
        ConfirmModal,
    },
    mixins: [FormBaseMixin],
    props: {
        storeRestaurantIds: {
            type: Array,
            default: () => [],
        },
        refreshRestaurants: {
            type: Number,
            default: 0,
        },
    },
    data: () => ({
        api: RestApiType.BRANDS,
        mapper: MapperType.BRAND_FORM,
        form: {
            name: null,
            logo: null,
            cover: null,
            description: null,
            tags: [],
            restaurants: [],
            restaurantOptions: [],
        },
        newTag: '',
        editRoute: routeType.BRAND_EDIT,
        listRoute: routeType.BRAND_LIST,
        restaurantsNeedUpdate: false,
        storeWarningDialog: false,
        pendingRestaurants: null,
    }),
    computed: {
        brandId() {
            return this.entityId ? Number(this.entityId) : null
        },
    },
    watch: {
        refreshRestaurants() {
            this.restaurantsNeedUpdate = !this.restaurantsNeedUpdate
        },
        // the select options are usually cached and resolve before BaseForm loads the
        // brand; EntitySelectInput only applies items-filter inside updateList(), so
        // re-run it once the brand's own member list is known
        'form.restaurantOptions'() {
            this.restaurantsNeedUpdate = !this.restaurantsNeedUpdate
        },
    },
    methods: {
        restaurantItemsFilter(items) {
            // 1) a profile of another brand cannot be added here (spec D6); Vuetify honours item.disabled
            const filtered = items.map((item) => ({
                ...item,
                disabled:
                    item.brandId !== null && item.brandId !== this.brandId,
            }))
            // 2) keep members the select-option list omits (hidden + inactive profiles):
            //    EntitySelectInput prunes selected values it cannot find in its items
            const known = new Set(items.map((item) => item.key))
            const missingMembers = (this.form.restaurantOptions || []).filter(
                (option) => !known.has(option.key)
            )
            return [...missingMembers, ...filtered]
        },
        onRestaurantsInput(value) {
            const next = Array.isArray(value) ? value : []
            const previous = this.form.restaurants || []
            const removedStoreMember = previous.find(
                (iri) =>
                    !next.includes(iri) &&
                    this.storeRestaurantIds.includes(
                        this.restaurantIdFromIri(iri)
                    )
            )

            if (removedStoreMember) {
                this.pendingRestaurants = next
                this.storeWarningDialog = true
                return
            }

            this.form.restaurants = next
        },
        applyPendingRestaurants() {
            this.form.restaurants = this.pendingRestaurants
            this.pendingRestaurants = null
        },
        discardPendingRestaurants() {
            // re-emit the unchanged value so the selector's internal state follows the form again
            this.form.restaurants = [...(this.form.restaurants || [])]
            this.pendingRestaurants = null
        },
        restaurantIdFromIri(iri) {
            return Number(String(iri).split('/').pop())
        },
        updated() {
            this.$emit('updated')
            if (this.$route.query.redirectTo) {
                this.$router.push(this.$route.query.redirectTo)
            }
        },
        addTag() {
            if (this.newTag) {
                this.form.tags.push(this.newTag)
                this.newTag = ''
            }
        },
        removeTag(tag) {
            this.form.tags = this.form.tags.filter((t) => t !== tag)
        },
    },
}
</script>
